/**
 * B-050 — one benchmark's legend entry on a performance chart (AgentsPage and NavChart): the line's swatch, the
 * symbol and its return over the chart's window, with "*" while that window's last close is pending (as on the
 * Scoreboard). Never a 0 for something unknown:
 * - "unavailable" when the route could not serve the series (`_failed`, or the request failed);
 * - "—" when the window has fewer than two of its closes;
 * - "to Oct 5" after the value when its last close is older than the chart's last day (the funds are stored by
 *   the 5 PM ET refresh, so during the day they end a session before the agents and SPY).
 */
import React from 'react';
import { Benchmark, WindowReturn } from '../../utils/benchmarks';
import { formatDateShort, formatSignedPct } from '../../utils/format';

interface Props {
  benchmark: Benchmark;
  value: WindowReturn | null;
  failed?: boolean;
  /** The chart's last day (YYYY-MM-DD). */
  chartEnd?: string;
  wide?: boolean;
}

const BenchmarkLegendItem: React.FC<Props> = ({ benchmark, value, failed = false, chartEnd, wide = false }) => (
  <span className="flex items-center gap-1.5 text-10" data-testid={`benchmark-legend-${benchmark.symbol}`}>
    <span
      className={wide ? 'w-4 h-[2px] inline-block rounded-sm' : 'w-3 h-[2px] inline-block rounded'}
      style={{ backgroundColor: benchmark.color }}
    />
    <span className="font-medium" style={{ color: benchmark.color, opacity: 0.85 }}>
      {benchmark.symbol}{' '}
      {failed ? (
        <span className="text-slate-500" title="The stored series could not be read">unavailable</span>
      ) : value ? (
        <>
          {formatSignedPct(value.pct)}
          {value.pending && <span title="The latest close is still provisional">*</span>}
          {chartEnd !== undefined && value.end < chartEnd && (
            <span className="text-slate-500"> to {formatDateShort(value.end)}</span>
          )}
        </>
      ) : (
        <span className="text-slate-500" title="Fewer than two closes in this window">—</span>
      )}
    </span>
  </span>
);

export default BenchmarkLegendItem;
